import React from "react";
import PropTypes from "prop-types";
import { Highlight, Snippet } from "react-instantsearch";
import { unescapeHtml } from "./highlightTruncate";

// Renders a hit attribute with Algolia's match highlighting via react-instantsearch's
// <Highlight>/<Snippet>. When the hit has no _highlightResult/_snippetResult entry for the
// attribute -- a non-search render, or an attribute Algolia did not return -- falls back to the
// plain text/children rather than rendering nothing.

function getPath(hit, attribute, resultKey) {
  const result = hit && hit[resultKey];
  if (!result) return undefined;

  const path = Array.isArray(attribute) ? attribute : [attribute];
  return path.reduce((node, key) => (node == null ? undefined : node[key]), result);
}

// True when Algolia returned a non-"none" match for this attribute on this hit, so a card can swap
// a truncated/excerpted field for its highlighted full text only when there is a match to reveal.
export function attributeMatched(hit, attribute) {
  const entry = getPath(hit, attribute, "_highlightResult");
  if (entry === undefined) return false;

  const entries = Array.isArray(entry) ? entry : [entry];
  return entries.some((node) => node && node.matchLevel && node.matchLevel !== "none");
}

// The <mark>-tagged highlight string Algolia/react-instantsearch put in _highlightResult for an
// attribute, or undefined when absent. Cards that compose a partial highlight (e.g. the title part
// of a "<title> at <company>" headline) read this and pass a slice to <HighlightedText>.
export function highlightValue(hit, attribute) {
  const entry = getPath(hit, attribute, "_highlightResult");
  const node = Array.isArray(entry) ? entry[0] : entry;
  return node && typeof node.value === "string" ? node.value : undefined;
}

const MARK_SPLIT = /(<mark>.*?<\/mark>)/gis;
const MARK_CONTENT = /^<mark>(.*)<\/mark>$/is;

// Renders a <mark>-tagged highlight string (as found in _highlightResult.<attr>.value) into the
// same markup react-instantsearch's <Highlight> emits, so partial/composed highlights match. The
// value is HTML-escaped, so each segment is unescaped for display (else "Founder & CEO" renders as
// "Founder &amp; CEO"); React escapes the text children again on output, so this stays XSS-safe.
export function HighlightedText({ markup }) {
  const segments = markup.split(MARK_SPLIT).filter((segment) => segment !== "");

  return (
    <span className="ais-Highlight">
      {segments.map((segment, index) => {
        const marked = segment.match(MARK_CONTENT);
        const key = `${index}-${segment}`;
        return marked ? (
          <mark key={key} className="ais-Highlight-highlighted">
            {unescapeHtml(marked[1])}
          </mark>
        ) : (
          <span key={key} className="ais-Highlight-nonHighlighted">
            {unescapeHtml(segment)}
          </span>
        );
      })}
    </span>
  );
}

HighlightedText.propTypes = {
  markup: PropTypes.string.isRequired,
};

// Renders pre-split highlight parts into the same markup react-instantsearch's <Highlight> emits, for
// callers that truncate a highlighted value (see highlightTruncate.js).
export function HighlightedParts({ parts }) {
  let offset = 0;
  return (
    <span className="ais-Highlight">
      {parts.map((part) => {
        const key = `${offset}-${part.highlighted ? "h" : "n"}`;
        offset += part.value.length;
        return part.highlighted ? (
          <mark key={key} className="ais-Highlight-highlighted">
            {part.value}
          </mark>
        ) : (
          <span key={key} className="ais-Highlight-nonHighlighted">
            {part.value}
          </span>
        );
      })}
    </span>
  );
}

HighlightedParts.propTypes = {
  parts: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string.isRequired, highlighted: PropTypes.bool }))
    .isRequired,
};

function HighlightAttribute({ hit, attribute, snippet = false, fallback = null }) {
  const resultKey = snippet ? "_snippetResult" : "_highlightResult";

  if (getPath(hit, attribute, resultKey) === undefined) {
    return fallback === null ? null : fallback;
  }

  if (snippet) {
    return <Snippet hit={hit} attribute={attribute} highlightedTagName="mark" />;
  }

  return <Highlight hit={hit} attribute={attribute} highlightedTagName="mark" />;
}

HighlightAttribute.propTypes = {
  hit: PropTypes.instanceOf(Object).isRequired,
  attribute: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]).isRequired,
  snippet: PropTypes.bool,
  fallback: PropTypes.node,
};

export default HighlightAttribute;
